/**
 * In-app operator guide — how to USE the admin panel end to end. Static content;
 * mirrors the workflow the content pipeline (PRE-165) expects. Keep it in sync
 * with the other screens when the flow changes.
 *
 * The prose lives in the message catalogs rather than in this file, and the
 * screen is a renderer over a small table of section keys. The English original
 * carried inline `<strong>`/`<Code>` emphasis mid-sentence; keeping that would
 * have meant splitting each sentence into three keys, which is not something a
 * translator can read or a reviewer can check. Whole sentences translate; halves
 * of sentences do not.
 */
import { Card, List, Stack, Text, Title } from '@mantine/core';

import { useT, type MessageKey } from '../i18n';

/** One card: a heading, an optional lead paragraph, bullets, an optional note. */
type Section = {
  title: MessageKey;
  intro?: MessageKey;
  items?: MessageKey[];
  /** A second paragraph between two bullet lists (Review has one). */
  mid?: MessageKey;
  midItems?: MessageKey[];
  note?: MessageKey;
};

const SECTIONS: Section[] = [
  {
    title: 'guide.access.title',
    items: ['guide.access.1', 'guide.access.2'],
  },
  {
    title: 'guide.templates.title',
    intro: 'guide.templates.intro',
    items: ['guide.templates.1', 'guide.templates.2', 'guide.templates.3', 'guide.templates.4'],
  },
  {
    title: 'guide.generate.title',
    intro: 'guide.generate.intro',
    items: ['guide.generate.1', 'guide.generate.2', 'guide.generate.3'],
    note: 'guide.generate.note',
  },
  {
    title: 'guide.review.title',
    intro: 'guide.review.intro',
    items: ['guide.review.1', 'guide.review.2', 'guide.review.3'],
    mid: 'guide.review.advisory',
    midItems: ['guide.review.4', 'guide.review.5', 'guide.review.6'],
    note: 'guide.review.note',
  },
  {
    title: 'guide.reset.title',
    intro: 'guide.reset.intro',
    items: ['guide.reset.1', 'guide.reset.2', 'guide.reset.3'],
  },
  {
    title: 'guide.content.title',
    intro: 'guide.content.body',
    note: 'guide.content.note',
  },
  {
    title: 'guide.roadmaps.title',
    intro: 'guide.roadmaps.body',
  },
  {
    title: 'guide.goal.title',
    intro: 'guide.goal.body',
  },
  {
    title: 'guide.emails.title',
    items: ['guide.emails.1', 'guide.emails.2', 'guide.emails.3'],
  },
  {
    title: 'guide.trouble.title',
    items: ['guide.trouble.1', 'guide.trouble.2', 'guide.trouble.3', 'guide.trouble.4'],
  },
];

export function GuideScreen() {
  const t = useT();

  return (
    <Stack gap="md">
      <Text c="dimmed" size="sm">
        {t('guide.intro')}
      </Text>

      {SECTIONS.map((section) => (
        <Card key={section.title} withBorder padding="lg" radius="md">
          <Title order={4} mb="sm">
            {t(section.title)}
          </Title>
          {section.intro && <Text mb={section.items ? 'sm' : 0}>{t(section.intro)}</Text>}
          {section.items && (
            <List spacing="xs">
              {section.items.map((key) => (
                <List.Item key={key}>{t(key)}</List.Item>
              ))}
            </List>
          )}
          {section.mid && <Text my="sm">{t(section.mid)}</Text>}
          {section.midItems && (
            <List spacing="xs">
              {section.midItems.map((key) => (
                <List.Item key={key}>{t(key)}</List.Item>
              ))}
            </List>
          )}
          {section.note && (
            <Text c="dimmed" size="sm" mt="sm">
              {t(section.note)}
            </Text>
          )}
        </Card>
      ))}
    </Stack>
  );
}
